import {useEffect, useState} from 'react'
import GroupFormCardSet from '../3modules/Iam/GroupFormCardSet'
import {useNavigate, useParams} from 'react-router-dom'
import {validateDescription, validateGroupName} from '../../services/util/editChecks'
import {
    checkSelected, createTerritoryCheckList,
    deepCopyObjectArray,
    extractItemsFromArray,
    extractSelectedItemsFromArray,
    extractValuesFromObjectArray,
    getFeatureDetailsByNames, isAllProvincesAreChecked,
    populateAssignedUserForEdit,
    populateBusinessesDataForEdit, populateOwnerForEdit,
    populateTerritoriesDataForEdit, selectAllProvinces,
} from '../../services/util/miscellaneousService'
import ApiService from '../../common/ApiCalls'
import {addedRemovedDetector, selectedUnselectedDetector} from '../../services/util/formatter'
import FormSuccessSnackbar from '../2compounds/FormSuccessSnackbar'
import FormErrorSnackbar from '../2compounds/FormErrorSnackbar'
import LoaderBackdrop from '../2compounds/LoaderBackdrop'

const GroupCreateFormTemplate = ({groupDetails, edit}) => {
    const navigate = useNavigate()
    const groupId = useParams().groupId
    const [name, setName] = useState('')
    const [nameOriginal, setNameOriginal] = useState('')
    const [description, setDescription] = useState('')
    const [descriptionOriginal, setDescriptionOriginal] = useState('')
    const [owner, setOwner] = useState(null)
    const [existingOwner, setExistingOwner] = useState('')
    const [featureSetsOriginal, setFeatureSetsOriginal] = useState([])
    const [usersOriginal, setUsersOriginal] = useState([])
    const [featureSets, setFeatureSets] = useState([])
    const [territoriesOriginal, setTerritoriesOriginal] = useState([])
    const [territories, setTerritories] = useState([])
    const [businessesOriginal, setBusinessesOriginal] = useState([])
    const [businesses, setBusinesses] = useState([])
    const [users, setUsers] = useState([])
    const [showError, setShowError] = useState(false)
    const [isSnackbarOpenSuccess, setIsSnackbarOpenSuccess] = useState(false)
    const [isSnackbarOpenFail, setIsSnackbarOpenFail] = useState(false)
    const [allProvincesAreSelected, setAllProvincesAreSelected] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [loaded, setLoaded] = useState(false)

    const groupDataChanged = () => {
        if(name !== nameOriginal) return true
        if(description !== descriptionOriginal) return true
        return owner !== existingOwner
    }

    const hasError = async () => {
        const nameHasError = await validateGroupName(name, edit, nameOriginal)
        const descriptionHasError = validateDescription(description)
        const ownerHasError = !Boolean(owner)
        const featureSetsHasError = !checkSelected(featureSets)
        const territoriesHasError = !checkSelected(territories)
        const usersLength = owner ? users.length + 1 : users.length
        const usersHasError = !usersLength
        return nameHasError||descriptionHasError||ownerHasError||featureSetsHasError||territoriesHasError||usersHasError
    }

    const createGroup = async (name, description, ownerUserId, resourceIdList, featureIdList, userIdList) => {
        return await ApiService().createGroup(name, description, ownerUserId, resourceIdList, featureIdList, userIdList)
    }

    const updateGroup = async (name, description, ownerUserId, existingOwnerId) => {
        // Update name, description and owner
        if(groupDataChanged()){
            await ApiService().updateGroup(name, description, ownerUserId, existingOwnerId, groupId)
        }

        // update users
        const {added: addedUsers, removed: removedUsers} = addedRemovedDetector(usersOriginal, users)
        const addedUserIdList = extractItemsFromArray(addedUsers, 'user_id')
        if(addedUserIdList.length> 0) await ApiService().assignUsersToGroup(groupId, addedUserIdList)
        const removedUserIdList = extractItemsFromArray(removedUsers, 'user_id')
        if(removedUserIdList.length> 0) await ApiService().removeUsersFromGroup(name, groupId, removedUserIdList)

        // update featureSets
        const {selected: selectedFeatures, unselected: unselectedFeatures} = selectedUnselectedDetector(featureSetsOriginal, featureSets)
        const selectedFeaturesIdList = extractItemsFromArray(selectedFeatures, 'feature_id')
        if(selectedFeaturesIdList.length > 0) await ApiService().assignFeaturesToGroup(groupId, selectedFeaturesIdList)
        const unselectedFeaturesIdList = extractItemsFromArray(unselectedFeatures, 'feature_id')
        if(unselectedFeaturesIdList.length > 0) await ApiService().removeFeaturesFromGroup(name, groupId, unselectedFeaturesIdList)

        // update resource set
        const {selected: selectedTerritories, unselected: unselectedTerritories} = selectedUnselectedDetector(territoriesOriginal, territories)
        const {added: addedBusinesses, removed: removedBusinesses} = addedRemovedDetector(businessesOriginal, businesses)
        const addedResourceList = selectedTerritories.concat(addedBusinesses)
        const addedResourceIdList = extractItemsFromArray(addedResourceList, 'resource_id')
        const removedResourceList = unselectedTerritories.concat(removedBusinesses)
        const removedResourceIdList = extractItemsFromArray(removedResourceList, 'resource_id')
        if(addedResourceIdList.length > 0) await ApiService().assignResourcesToGroup(groupId, addedResourceIdList)
        if(removedResourceIdList.length > 0) await ApiService().removeResourcesFromGroup(name, groupId, removedResourceIdList)
    }

    const saveHandler = async () => {
        setShowError(true)
        const error = await hasError()

        if(!error){
            setIsLoading(true)
            setShowError(false)
            const ownerUserId = owner.user_id
            const existingOwnerId = existingOwner.user_id
            const territoryIdList = extractSelectedItemsFromArray(territories, 'resource_id')
            const businessIdList = extractItemsFromArray(businesses, 'resource_id')
            const resourceIdList = territoryIdList.concat(businessIdList)
            const featureIdList = extractSelectedItemsFromArray(featureSets, 'feature_id')
            const userIdList = extractValuesFromObjectArray(users, 'user_id')

            if(edit){
                updateGroup(name, description, ownerUserId, existingOwnerId)
                    .then(()=>{
                        setLoaded(true)
                        setIsSnackbarOpenSuccess(true)
                        setTimeout(()=>{
                            navigate('/adminDashboard')
                        }, 1000)
                    })
            }else{
                createGroup(name, description, ownerUserId, resourceIdList, featureIdList, userIdList)
                    .then(()=>{
                        setLoaded(true)
                        setIsSnackbarOpenSuccess(true)
                        setTimeout(()=>{
                            navigate('/adminDashboard')
                        }, 1000)
                    })
            }

        }else{
            setIsSnackbarOpenFail(true)
        }
    }

    const cancelHandler = () => {
        navigate(-1)
    }

    useEffect(()=>{
        if(groupDetails){
            // Populate input for an existing group
            const {group_name, group_description, owner, features, resources, users} = groupDetails
            setName(group_name)
            setNameOriginal(group_name)
            setDescription(group_description)
            setDescriptionOriginal(group_description)

            populateOwnerForEdit(owner)
                .then((res)=>{
                    setOwner(res)
                    setExistingOwner(res)
                })

            getFeatureDetailsByNames(features)
                .then((selectedFeatureSets)=>{
                    setFeatureSets(selectedFeatureSets)
                    setFeatureSetsOriginal(deepCopyObjectArray(selectedFeatureSets))
                })

            populateTerritoriesDataForEdit(resources)
                .then((selectedTerritories)=>{
                    const deepCopiedTerritories = deepCopyObjectArray(selectedTerritories)
                    if(deepCopiedTerritories[1].selected&&!allProvincesAreSelected){
                        selectAllProvinces(deepCopiedTerritories)
                    }
                    setTerritories(deepCopiedTerritories)
                    setTerritoriesOriginal(deepCopyObjectArray(selectedTerritories))
                })
                .then(()=>{

                })

            populateBusinessesDataForEdit(resources)
                .then((selectedBusinesses)=>{
                    setBusinesses(selectedBusinesses)
                    setBusinessesOriginal(selectedBusinesses)
                })

            populateAssignedUserForEdit(users)
                .then((assignedUserList)=>{
                    setUsers(assignedUserList)
                    setUsersOriginal(assignedUserList)
                })
        }else{
            if(territories.length === 0){
                createTerritoryCheckList()
                    .then((checkList)=>{
                        setTerritories(checkList)
                    })
            }
        }
    // XXX Fix this in the future
    // eslint-disable-next-line
    }, [])

    useEffect(()=>{
            setAllProvincesAreSelected(isAllProvincesAreChecked(territories))
    }, [territories])

    useEffect(()=>{
        if(territories.length){
            const deepCopiedTerritories = deepCopyObjectArray(territories)
            deepCopiedTerritories[1].selected = !allProvincesAreSelected
            setTerritories(deepCopiedTerritories)
        }
    // XXX Fix this in the future
    // eslint-disable-next-line
    }, [allProvincesAreSelected])

    return (
        <>
            <GroupFormCardSet nameValue={name}
                              nameOriginal={nameOriginal}
                              setNameValue={setName}
                              descriptionValue={description}
                              setDescriptionValue={setDescription}
                              owner={owner}
                              setOwner={setOwner}
                              featureSets={featureSets}
                              setFeatureSets={setFeatureSets}
                              territories={territories}
                              setTerritories={setTerritories}
                              businesses={businesses}
                              setBusinesses={setBusinesses}
                              users={users}
                              setUsers={setUsers}
                              showError={showError}
                              saveHandler={saveHandler}
                              cancelHandler={cancelHandler}
                              edit />
            <FormSuccessSnackbar isSnackbarOpen={isSnackbarOpenSuccess} setIsSnackbarOpen={setIsSnackbarOpenSuccess} />
            <FormErrorSnackbar isSnackbarOpen={isSnackbarOpenFail} setIsSnackbarOpen={setIsSnackbarOpenFail} />
            <LoaderBackdrop open={isLoading} loaded={loaded} />
        </>

    )
}

export default GroupCreateFormTemplate
